import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { CdnBaseUrl } from '@deltek/specification-client/core/Utilities/urlUtils'
import Avatar from '@material-ui/core/Avatar'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import { CategoryPanel, ProfessionalDisciplineInner } from 'models/category'
import React, { ChangeEvent } from 'react'

const categoryStyles = makeStyles((theme: Theme) => ({
    selectLabel: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    text: {
        fontSize: pxToRem(13),
    },

    root: {
        border: 'none',
        paddingLeft: 0,
        '& .MuiAutocomplete-tag': {
            fontSize: pxToRem(13),
        },

        '&:focus': {
            backgroundColor: theme.palette.common.white,
            outline: 'none',
        },
    },
    option: { wordBreak: 'break-word' },
    categoryContainer: {
        padding: theme.spacing(3.125, 0.75, 3.125, 1.75),
    },
    categoriesDiv: {
        minHeight: 500,
        width: '100%',
    },
    headerText: {
        color: theme.palette.primary.dark,
        fontSize: pxToRem(14),
        marginTop: theme.spacing(1.5),
        fontWeight: 500,
        marginBottom: theme.spacing(1),
        textTransform: 'uppercase',
    },
    avatarIconContainerActive: {
        textTransform: 'uppercase',
        textDecoration: 'none',
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.common.white,
        fontWeight: 400,
        fontSize: pxToRem(18),
        width: 35,
        height: 35,
        display: 'inline-flex',
        border: `1px solid ${theme.palette.secondary.main}`,
    },
    avatarIconContainer: {
        textTransform: 'uppercase',
        textDecoration: 'none',
        color: theme.palette.common.white,
        backgroundColor: theme.palette.secondary.main,
        fontWeight: 400,
        fontSize: pxToRem(18),
        width: 35,
        height: 35,
        display: 'inline-flex',
        border: `1px solid ${theme.palette.secondary.main}`,
    },
    categoryItem: {
        width: '100%',
        height: 42,
        border: '1px solid #DBDFE0',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        padding: 5,
        backgroundColor: theme.palette.common.white,
        margin: theme.spacing(0, 0, 0.75, 0),
        cursor: 'pointer',
    },
    categoryItemActive: {
        width: '100%',
        height: 42,
        border: '1px solid #DBDFE0',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        padding: 5,
        backgroundColor: theme.palette.secondary.main,
        margin: theme.spacing(0, 0, 0.75, 0),
        cursor: 'pointer',
    },
    categoryItemAllActive: {
        width: '100%',
        height: 42,
        border: '1px solid #DBDFE0',
        borderRadius: 4,
        display: 'flex',
        alignItems: 'center',
        padding: 5,
        backgroundColor: theme.palette.secondary.main,
        margin: theme.spacing(0, 0, 0.75, 0),
        cursor: 'pointer',
    },
    categoryImage: {
        width: 35,
        height: 35,
        borderRadius: 50,
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
    },
    categoryText: {
        marginLeft: theme.spacing(1.25),
        fontSize: pxToRem(14),
    },
    categoryTextActive: {
        marginLeft: theme.spacing(1.25),
        color: theme.palette.common.white,
        fontSize: pxToRem(14),
    },

    disciplineContainer: {
        width: '100%',
    },
    dropIcon: {
        color: '#000000 54%',
        cursor: 'pointer',
    },
    selectInner: {
        height: 11,
        fontSize: pxToRem(13),
        paddingLeft: '0 !important',
        '&:focus': {
            backgroundColor: theme.palette.common.white,
            outline: 'none',
        },
    },
    selectDiscipline: {
        background: theme.palette.common.white,
        border: 'none',
        borderRadius: 4,
        width: '100%',
        height: 31,
        marginBottom: theme.spacing(1.5),
        fontSize: pxToRem(13),
        '&:focus': {
            backgroundColor: theme.palette.common.white,
            outline: 'none',
        },
    },
}))
interface CategoriesPanelProps {
    handleCategorySelection: (
        dataCategory: CategoryPanel,
        dataDiscipline: ProfessionalDisciplineInner
    ) => void
    disciplineData: ProfessionalDisciplineInner[]
    categoriesData: CategoryPanel[]
    selectedCategory?: CategoryPanel | null
    selectedDiscipline?: ProfessionalDisciplineInner | null
    defaultCategory?: CategoryPanel
    selectedElementFilterValue?: string
}
const CategoriesPanel: React.FC<CategoriesPanelProps> = ({
    handleCategorySelection,
    disciplineData,
    categoriesData,
    selectedCategory,
    selectedDiscipline,
    selectedElementFilterValue,
}) => {
    const classes = categoryStyles()

    const handleCategoryChange = React.useCallback(
        (
            dataCategory: CategoryPanel,
            dataDiscipline: ProfessionalDisciplineInner
        ) => {
            if (dataCategory && dataDiscipline) {
                handleCategorySelection(dataCategory, dataDiscipline)
            }
        },
        [handleCategorySelection]
    )

    const handleDisciplineChange = (
        _: ChangeEvent<{}>,
        values: ProfessionalDisciplineInner
    ) => {
        handleCategorySelection(values.categories[0], values)
    }

    React.useEffect(() => {
        if (!selectedCategory && !selectedDiscipline && disciplineData[0]) {
            if (handleCategoryChange) {
                if (selectedElementFilterValue === 'Added to Work Results') {
                    handleCategoryChange(
                        {
                            name: 'Work Results',
                            id: '1f689538-1896-41a1-b66b-dd41f71a595a',
                        },
                        disciplineData[0]
                    )
                } else {
                    handleCategoryChange(
                        {
                            name: 'All Categories',
                            id: 'all',
                        },
                        disciplineData[0]
                    )
                }
            }
        }
    }, [
        disciplineData,
        categoriesData,
        handleCategoryChange,
        selectedCategory,
        selectedDiscipline,
        selectedElementFilterValue,
    ])

    return (
        <GridContainer
            data-testid="categoryContainer"
            className={classes.categoryContainer}
        >
            <GridItem
                data-testid="AllCategoryButton"
                className={
                    selectedCategory?.name === 'All Categories'
                        ? classes.categoryItemAllActive
                        : classes.categoryItem
                }
                onClick={() => {
                    if (selectedCategory?.name !== 'All Categories') {
                        handleCategoryChange(
                            {
                                name: 'All Categories',
                                id: 'all',
                            },
                            disciplineData[0]
                        )
                    }
                }}
            >
                <Avatar
                    alt="All Categories"
                    className={
                        selectedCategory?.name === 'All Categories'
                            ? classes.avatarIconContainerActive
                            : classes.avatarIconContainer
                    }
                >
                    AC
                </Avatar>
                <Typography
                    component="span"
                    className={
                        selectedCategory?.name === 'All Categories'
                            ? classes.categoryTextActive
                            : classes.categoryText
                    }
                >
                    All Categories
                </Typography>
            </GridItem>
            <GridItem className={classes.headerText}>Categories</GridItem>

            {!!selectedDiscipline && (
                <GridItem className={classes.disciplineContainer}>
                    <Autocomplete
                        data-testid="disciplineWrapper"
                        className={classes.selectDiscipline}
                        noOptionsText="No Discipline"
                        options={disciplineData}
                        getOptionSelected={(
                            option: ProfessionalDisciplineInner,
                            value: ProfessionalDisciplineInner
                        ) => option.name === value.name}
                        selectOnFocus={false}
                        value={selectedDiscipline}
                        popupIcon={
                            <ExpandMoreIcon
                                data-testid="arrowIcon"
                                fontSize="small"
                                className={classes.dropIcon}
                            />
                        }
                        getOptionLabel={(option: ProfessionalDisciplineInner) =>
                            option.name
                        }
                        onChange={handleDisciplineChange}
                        disableClearable={true}
                        blurOnSelect={true}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                data-testid="disciplineSelector"
                                size="small"
                                fullWidth={true}
                                label="Discipline"
                                inputProps={{
                                    ...params.inputProps,
                                    'aria-autocomplete': 'none',
                                    autoComplete: 'off',
                                    'data-testid': 'disciplineInput',
                                }}
                                variant="outlined"
                                InputLabelProps={{
                                    ...params.InputLabelProps,
                                    className: clsx(
                                        classes.text,
                                        classes.selectLabel
                                    ),
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    classes: {
                                        input: classes.selectInner,
                                        root: classes.root,
                                    },
                                }}
                            />
                        )}
                    />
                </GridItem>
            )}

            <div className={classes.categoriesDiv}>
                {selectedDiscipline &&
                    categoriesData
                        .slice()
                        .sort((a: CategoryPanel, b: CategoryPanel) => {
                            if (a.name > b.name) {
                                return 1
                            } else if (a.name < b.name) {
                                return -1
                            }
                            return 0
                        })
                        ?.map((item: CategoryPanel, index: number) => {
                            return (
                                <GridItem
                                    data-testid="CategoryItem"
                                    className={
                                        selectedCategory?.name === item?.name
                                            ? classes.categoryItemActive
                                            : classes.categoryItem
                                    }
                                    key={index}
                                    onClick={() =>
                                        handleCategoryChange(
                                            item,
                                            selectedDiscipline
                                        )
                                    }
                                >
                                    <div
                                        style={{
                                            backgroundImage: `url(${CdnBaseUrl}/specpoint/ae/categories/${item.name
                                                .toLocaleLowerCase()
                                                .replace(/\s/g, '_')}.jpg)`,
                                        }}
                                        className={classes.categoryImage}
                                    ></div>
                                    <Typography
                                        className={
                                            selectedCategory?.name ===
                                            item?.name
                                                ? classes.categoryTextActive
                                                : classes.categoryText
                                        }
                                    >
                                        {item.name}
                                    </Typography>
                                </GridItem>
                            )
                        })}
            </div>
        </GridContainer>
    )
}

export default CategoriesPanel
